import * as i0 from '@angular/core';
import { Injectable, Optional, Inject, NgModule } from '@angular/core';
import { DateFnsModule, DateFnsAdapter } from '@angular/material-date-fns-adapter';
import * as i1 from '@angular/material/core';
import { MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';
import { DatetimeAdapter, MTX_DATETIME_FORMATS } from '@ng-matero/extensions/core';
import { getHours, getMinutes, isValid, startOfMonth, addHours, addMinutes, addMonths } from 'date-fns';

function range(length, valueFunction) {
    const valuesArray = Array(length);
    for (let i = 0; i < length; i++) {
        valuesArray[i] = valueFunction(i);
    }
    return valuesArray;
}
class DateFnsDateTimeAdapter extends DatetimeAdapter {
    constructor(matDateLocale, _delegate) {
        super(_delegate);
        this.setLocale(matDateLocale);
    }
    setLocale(locale) {
        super.setLocale(locale);
    }
    getHour(date) {
        return getHours(date);
    }
    getMinute(date) {
        return getMinutes(date);
    }
    isInNextMonth(startDate, endDate) {
        const nextMonth = this.getDateInNextMonth(startDate);
        return super.sameMonthAndYear(nextMonth, endDate);
    }
    createDatetime(year, month, day, hour, minute) {
        if (month < 0 || month > 11) {
            throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
        }
        if (day < 1) {
            throw Error(`Invalid date "${day}". Date has to be greater than 0.`);
        }
        if (hour < 0 || hour > 23) {
            throw Error(`Invalid hour "${hour}". Hour has to be between 0 and 23.`);
        }
        if (minute < 0 || minute > 59) {
            throw Error(`Invalid minute "${minute}". Minute has to be between 0 and 59.`);
        }
        const result = new Date(year, month, day, hour, minute);
        if (!isValid(result)) {
            throw Error(`Invalid date "${day}" for month with index "${month}".`);
        }
        return result;
    }
    getFirstDateOfMonth(date) {
        return startOfMonth(date);
    }
    getHourNames() {
        return range(24, i => i.toLocaleString(this.locale));
    }
    getMinuteNames() {
        return range(60, i => i.toLocaleString(this.locale));
    }
    addCalendarHours(date, hours) {
        return addHours(date, hours);
    }
    addCalendarMinutes(date, minutes) {
        return addMinutes(date, minutes);
    }
    deserialize(value) {
        return this._delegate.deserialize(value);
    }
    getDateInNextMonth(date) {
        return addMonths(date, 1);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DateFnsDateTimeAdapter, deps: [{ token: MAT_DATE_LOCALE, optional: true }, { token: i1.DateAdapter }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DateFnsDateTimeAdapter }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DateFnsDateTimeAdapter, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [MAT_DATE_LOCALE]
                }] }, { type: i1.DateAdapter }] });

const MTX_DATE_FNS_FORMATS = {
    parse: {
        dateInput: 'P',
        monthInput: 'LLLL',
        yearInput: 'yyyy',
        datetimeInput: 'P p',
        timeInput: 'p',
    },
    display: {
        dateInput: 'P',
        monthInput: 'LLLL',
        yearInput: 'yyyy',
        datetimeInput: 'P p',
        timeInput: 'p',
        monthYearLabel: 'yyyy',
        dateA11yLabel: 'LLLL dd, yyyy',
        monthYearA11yLabel: 'MMMM yyyy',
        popupHeaderDateLabel: 'ccc, dd LLL',
    },
};

class DateFnsDatetimeModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DateFnsDatetimeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.1", ngImport: i0, type: DateFnsDatetimeModule, imports: [DateFnsModule] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DateFnsDatetimeModule, providers: [{ provide: DatetimeAdapter, useClass: DateFnsDateTimeAdapter }], imports: [DateFnsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DateFnsDatetimeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DateFnsModule],
                    providers: [{ provide: DatetimeAdapter, useClass: DateFnsDateTimeAdapter }],
                }]
        }] });
function provideDateFnsDatetimeAdapter(formats = MTX_DATE_FNS_FORMATS) {
    return [
        {
            provide: DateAdapter,
            useClass: DateFnsAdapter,
            deps: [MAT_DATE_LOCALE],
        },
        { provide: DatetimeAdapter, useClass: DateFnsDateTimeAdapter },
        { provide: MTX_DATETIME_FORMATS, useValue: formats },
    ];
}
class MtxDateFnsDatetimeModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: MtxDateFnsDatetimeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.1", ngImport: i0, type: MtxDateFnsDatetimeModule }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: MtxDateFnsDatetimeModule, providers: [provideDateFnsDatetimeAdapter()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: MtxDateFnsDatetimeModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [provideDateFnsDatetimeAdapter()],
                }]
        }] });
/**
 * @deprecated Use `MtxDateFnsDatetimeModule` instead.
 */
const MatDateFnsDatetimeModule = MtxDateFnsDatetimeModule;

/**
 * Generated bundle index. Do not edit.
 */

export { DateFnsDateTimeAdapter, DateFnsDatetimeModule, MTX_DATE_FNS_FORMATS, MatDateFnsDatetimeModule, MtxDateFnsDatetimeModule, provideDateFnsDatetimeAdapter };
